// Rutgers University-Newark: public college page (#2739), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Rutgers-Newark's newest posted Common Data Set is 2023-2024 (Office of
//    Institutional Research and Decision Support, Common Data Set page,
//    "Newark CDS_2023-2024_final_V1.pdf"; no 2024-2025 or 2025-2026 Newark
//    file is posted), so it is not used (the UMass Boston precedent). The
//    first-year counts come from the U.S. Department of Education's IPEDS
//    Admissions survey for Fall 2024, first-time degree-seeking students
//    (College Navigator and the ADM2024 complete data file, unit 186399:
//    APPLCN 36,793, ADMSSN 26,254, ENRLT 1,857). IPEDS gives no residency
//    split and no early action counts, so this page has neither.
//  - admissions.rutgers.edu: Application Dates and Deadlines for Newark (the
//    Fall/September 2027 table), Required Credentials for First-Year
//    Applicants (the Newark test policy, EOF) and How Rutgers Makes
//    Decisions (notification dates, high school course requirements).
//  - School of Arts and Sciences-Newark, 7-Year BA/MD program page.
//  - Rutgers Scarlet Hub, Rutgers Students Cost of Attendance: the 2026-2027
//    In-State and Out-of-State Newark Full-Time Undergraduate budgets
//    (scarlethub.rutgers.edu PDFs linked from that page), Newark College of
//    Arts and Sciences, on campus. Its items sum exactly to its totals.
//  - College Scorecard API, school id 186399 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    ipeds: {
        label: 'IPEDS Admissions, Fall 2024, U.S. Department of Education',
        url: 'https://nces.ed.gov/collegenavigator/?id=186399#admsns',
        read: '2026-10-01',
    },
    dates: {
        label: 'Rutgers Undergraduate Admissions, Application Dates and Deadlines, Newark',
        url: 'https://admissions.rutgers.edu/apply/dates-deadlines/newark',
        read: '2026-10-01',
    },
    firstyear: {
        label: 'Rutgers Undergraduate Admissions, Required Credentials for First-Year Applicants',
        url: 'https://admissions.rutgers.edu/apply/first-year-applicants',
        read: '2026-10-01',
    },
    decisions: {
        label: 'Rutgers Undergraduate Admissions, How We Make Decisions',
        url: 'https://admissions.rutgers.edu/apply/how-we-make-decisions',
        read: '2026-10-01',
    },
    bamd: {
        label: 'School of Arts and Sciences-Newark, 7-Year BA/MD Program',
        url: 'https://sasn.rutgers.edu/student-support/current-students/pre-professional-programs/bamd-program-njms-nwk',
        read: '2026-10-01',
    },
    coa: {
        label: 'Rutgers Scarlet Hub, Rutgers Students Cost of Attendance, 2026-2027',
        url: 'https://scarlethub.rutgers.edu/financial-services/cost-of-attendance/rutgers-students-cost-of-attendance/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?186399-Rutgers-University-Newark',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'rutgers-university-newark',
    kbId: 'rutgers_university_newark',
    name: 'Rutgers University-Newark',
    shortName: 'Rutgers-Newark',
    location: 'Newark, NJ',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Rutgers identity guidelines); decoration only.
    branding: {
        primary: '#CC0033',
        secondary: '#000000',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/rutgers_university_newark_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: null,
        applied: 36793,
        admitted: 26254,
        enrolled: 1857,
        source: 'ipeds',
        where: 'first-time, degree-seeking first-year students',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Rutgers-Newark has not posted a Common Data Set since 2023-2024, so these counts come from the Department of Education\'s IPEDS survey for Fall 2024, which gives one first-year rate for the whole campus and none by school or major.',
        source: 'ipeds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'Rutgers-Newark has a nonbinding early action plan and no early decision plan: its first-year dates list only early action and regular decision. IPEDS gives no early action counts, so this page shows no early action acceptance rate.',
        source: 'dates',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to a school within Rutgers-Newark. Arts and sciences, business, criminal justice and public affairs ask for 16 academic high school courses: four years of English, two years of one foreign language, three years of math (Algebra I, Geometry and Algebra II), two years of science and five other academic courses. Some programs ask for more:',
        rows: [
            { school: 'School of Nursing-Newark', requirement: 'Sixteen academic courses with four years of English, three years of math (Algebra I, Geometry and Algebra II), a year each of biology and chemistry and seven other academic courses. No foreign language is required.', source: 'decisions' },
            { school: '7-year BA/MD (with New Jersey Medical School)', requirement: 'Apply to the Newark College of Arts and Sciences with "Med Joint BA/MD" as your academic interest, then send the supplemental application (essay, three letters of recommendation, resume) by November 1. You need at least a 1400 SAT or a 32 ACT from a test taken by October of senior year, and U.S. citizenship or permanent residency. The medical school makes the BA/MD decision; notifications go out in April.', source: 'bamd' },
            { school: 'Educational Opportunity Fund (EOF)', requirement: 'Open to eligible New Jersey residents. Select the EOF option on the Rutgers Application or Common App, then complete the EOF application.', source: 'firstyear' },
        ],
        source: 'decisions',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'December 1' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'By January 31', binding: false },
            { plan: 'Regular Decision', deadline: 'December 1', decision: 'By February 28', binding: false },
        ],
        note: 'Rutgers calls these due dates, not deadlines: you may apply later, but if you apply late or your file is still incomplete, you are withdrawn from consideration once your school meets its enrollment goals. Decisions are posted in the Rutgers Application Portal, and admitted students reply by May 1.',
        source: 'dates',
        binding: 'Early action is not binding and does not stop you from applying to other colleges.',
        bindingSource: 'dates',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-blind',
        blind: true,
        line: 'Rutgers-Newark is test-blind: for most applicants, SAT and ACT scores are not required and not considered. The exception is the 7-year BA/MD program, which requires at least one SAT or ACT score. Rutgers says it maintains its test-optional and test-blind policies "through 2027".',
        source: 'firstyear',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'dates',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [15381, 36831] },
            { item: 'Fees', amounts: [3275, 3275] },
            { item: 'Food and housing (on campus)', amounts: [21946, 21946] },
            { item: 'Books', amounts: [1504, 1504] },
            { item: 'Travel', amounts: [1026, 1598] },
            { item: 'Loan fees', amounts: [110, 110] },
            { item: 'Miscellaneous', amounts: [3704, 3704] },
        ],
        totals: [46946, 68968],
        note: 'Rutgers\' estimated budget for a full-time undergraduate in the Newark College of Arts and Sciences living on campus. Tuition or fees differ in the business school, criminal justice, nursing and University College; nursing tuition is $18,002 in-state. Commuters are budgeted $33,758 in-state and $55,208 out-of-state. Rutgers calls the 2026-2027 tuition, fees and food and housing estimates; final tuition and fees are set each July.',
        source: 'coa',
        netPrice: {
            average: 16013,
            byIncome: [
                { income: '$0 to $30,000', amount: 8829 },
                { income: '$30,001 to $48,000', amount: 11678 },
                { income: '$48,001 to $75,000', amount: 14193 },
                { income: '$75,001 to $110,000', amount: 19591 },
                { income: 'Over $110,000', amount: 27471 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Rutgers\' aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
